// apps/web/src/components/contact-form.tsx — Phase 4 plan 04-03.
//
// A5 contact inputs: name (required) + email OR phone (at least one
// required per BOOK-02). The form is a controlled component — the
// parent owns the values via the `onChange` callback. Errors are
// passed in via the `errors` prop so the parent decides which
// i18n key to apply (the validator in `wizard/schema.ts` emits the
// keys; this component just renders the message strings).
//
// We do NOT debounce here: the parent (A5) gates "Weiter" on
// `isValid`; onChange is called on every keystroke, the parent
// re-runs the validator, the button re-enables. The wire cost is
// 3 string comparisons per keystroke — well below the React render
// budget.

import { useTranslation } from "react-i18next";

export interface ContactFormErrors {
  name?: string | undefined;
  email?: string | undefined;
  phone?: string | undefined;
  contactRequired?: string | undefined;
}

export interface ContactFormValues {
  name: string;
  email: string;
  phone: string;
}

export interface ContactFormProps {
  defaultValue?: Partial<ContactFormValues> | undefined;
  onChange: (values: ContactFormValues) => void;
  errors?: ContactFormErrors | undefined;
}

export function ContactForm({
  defaultValue,
  onChange,
  errors,
}: ContactFormProps): JSX.Element {
  const { t } = useTranslation();

  function update(patch: Partial<ContactFormValues>) {
    const next: ContactFormValues = {
      name: defaultValue?.name ?? "",
      email: defaultValue?.email ?? "",
      phone: defaultValue?.phone ?? "",
      ...patch,
    };
    onChange(next);
  }

  return (
    <div className="space-y-3" data-testid="contact-form">
      <Field
        id="guest-name"
        label={t("wizard.a5.nameLabel")}
        type="text"
        autoComplete="name"
        value={defaultValue?.name ?? ""}
        onChange={(v) => update({ name: v })}
        error={errors?.name}
        testid="contact-name"
      />
      <Field
        id="guest-email"
        label={t("wizard.a5.emailLabel")}
        type="email"
        autoComplete="email"
        value={defaultValue?.email ?? ""}
        onChange={(v) => update({ email: v })}
        error={errors?.email}
        testid="contact-email"
      />
      <Field
        id="guest-phone"
        label={t("wizard.a5.phoneLabel")}
        type="tel"
        autoComplete="tel"
        value={defaultValue?.phone ?? ""}
        onChange={(v) => update({ phone: v })}
        error={errors?.phone}
        testid="contact-phone"
      />
      {errors?.contactRequired && (
        <p
          className="text-sm text-red-700"
          data-testid="contact-required-error"
          role="alert"
        >
          {errors.contactRequired}
        </p>
      )}
    </div>
  );
}

interface FieldProps {
  id: string;
  label: string;
  type: "text" | "email" | "tel";
  autoComplete?: string | undefined;
  value: string;
  onChange: (next: string) => void;
  error?: string | undefined;
  testid: string;
}

function Field({
  id,
  label,
  type,
  autoComplete,
  value,
  onChange,
  error,
  testid,
}: FieldProps): JSX.Element {
  return (
    <div className="flex flex-col gap-1">
      <label
        htmlFor={id}
        className="text-sm font-medium text-text"
        data-testid={`${testid}-label`}
      >
        {label}
      </label>
      <input
        id={id}
        type={type}
        autoComplete={autoComplete}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        aria-invalid={error ? true : undefined}
        data-testid={testid}
        className={`min-h-[44px] px-3 py-2 rounded-md border bg-bg text-text ${
          error ? "border-red-500" : "border-divider"
        }`}
      />
      {error && (
        <p
          className="text-xs text-red-700"
          data-testid={`${testid}-error`}
          role="alert"
        >
          {error}
        </p>
      )}
    </div>
  );
}
